import { useEffect, useState } from 'react';

function routeOf(hash: string): string {
  return hash.split('&')[0];
}

function parseParams(hash: string): Record<string, string> {
  return hash
    .split('&')
    .slice(1)
    .reduce<Record<string, string>>((acc, segment) => {
      const [key, value] = segment.split('=');
      if (key) {
        acc[key] = value ?? '';
      }
      return acc;
    }, {});
}

function buildHref(baseHash: string, params: Record<string, string>): string {
  const queryString = Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${baseHash}&${queryString}`;
}

// Kept out of the hook body on purpose. Inline, the assignment trips the
// `react-compiler/react-compiler` lint rule: "Writing to a variable defined
// outside a component or hook is not allowed. Consider using an effect" - the
// writers below are plain functions it cannot prove are only called from
// handlers and effects. The compiler itself is happy either way (both shapes
// report CompileSuccess), so this silences the rule rather than satisfying it.
function navigateTo(href: string): void {
  window.location.href = href;
}

function useNavParams() {
  'use memo';

  const [hash, setHash] = useState(() => location.hash);

  useEffect(() => {
    // The route this hook instance belongs to, captured when we start
    // listening. Legacy dojo owns the router and registers its own hashchange
    // listener before ours (frontend/aysist/sdbplanning.js). The first time a
    // view is visited it loads that view asynchronously, so the previous screen
    // is still mounted when our listener fires with the *next* route's hash.
    // Consumers then derive state - and a few of them write to the hash from an
    // effect - for a route they do not own, which dragged the user back (#2145).
    // Ignore those events: dojo unmounts us moments later, and the new screen
    // gets a fresh hook instance anchored on the new route.
    //
    // That unmount is load-bearing, and it is the one thing a consumer has to
    // get right: a consumer that outlives a change of route stays anchored on
    // the route it first saw, so it reads a stale hash and drops every write
    // from then on - silently, there is nothing to catch. Today every consumer
    // is rendered by a dojo view controller whose `beforeDeactivate` calls
    // `unmountReactComponentAtRootId` (see frontend/aysist/views/), so the
    // anchor never outlives its route. Mount one from a host that survives
    // navigation and that stops being true.
    //
    // NB: intentionally captured once, on mount - do not add deps.
    const current = location.hash;
    const anchor = routeOf(current);

    // `createRoot().render()` returns before React commits, so the hash can
    // move between the state initialiser above and this line - and that
    // hashchange is lost, we were not listening yet. Adopt it: the anchor is
    // taken from `current`, and `baseHash` has to describe the same route or
    // the write guard below would drop every write until the next hashchange.
    // No-op (React bails out) in the normal case where nothing moved.
    setHash(current);

    const onHashChange = () => {
      // No anchor (mounted without a hash): keep the "follow everything"
      // behaviour instead of freezing forever.
      if (anchor && routeOf(location.hash) !== anchor) return;
      setHash(location.hash);
    };

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const baseHash = routeOf(hash);
  const params = hash ? parseParams(hash) : null;

  // Both writers build on `baseHash`, which the filter above keeps anchored to
  // the route we mounted on. Once the browser has moved on - dojo unmounts us a
  // tick later - writing that anchored route back would either navigate the
  // user backwards or, with `blockRefresh`, silently rewrite the URL to a route
  // that is not on screen (no hashchange, so dojo never notices). Drop those
  // writes: filtering reads keeps `params`-driven effects quiet, but effects
  // keyed on redux or props can still fire after we were left (#2145).
  //
  // Deliberately checked against `baseHash` rather than a second copy of the
  // anchor: the route we compare is then the very route we would write, so the
  // check cannot pass for a href that points somewhere else.
  const isOnOwnRoute = () => !baseHash || routeOf(location.hash) === baseHash;

  // Both writers report whether the write landed. Dropping it silently is not
  // enough for callers that pair the hash write with another side effect - they
  // have no other way to tell, and would run the second half for a route they
  // no longer own (`ViewSwitch` persisting a default view, #2145).
  const setParams = (
    newParams: Record<string, string>,
    blockRefresh?: boolean
  ): boolean => {
    if (!isOnOwnRoute()) return false;

    const merged = { ...(params ?? {}), ...newParams };
    const href = buildHref(baseHash, merged);
    if (blockRefresh) {
      window.history.replaceState(null, document.title, href);
    } else {
      navigateTo(href);
    }
    return true;
  };

  const removeParam = (key: string): boolean => {
    if (!isOnOwnRoute()) return false;

    const updated = { ...(params ?? {}) };
    delete updated[key];
    navigateTo(buildHref(baseHash, updated));
    return true;
  };

  return { params, setParams, removeParam };
}

export default useNavParams;
